import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getInventorySummary } from "../../services/inventoryService";
import { formatCurrency } from "../../utils/currency";
import type { InventorySummary } from "../../types/models";
import { getApiErrorMessage } from "../../utils/apiError";

/**
 * Inventory summary dashboard widget (Story 3.6).
 * Displays Total SKUs, Items Needing Attention, Total Value.
 * Clickable to navigate to /inventory.
 */
export function InventorySummaryWidget() {
  const { t } = useTranslation("inventory");
  const [summary, setSummary] = useState<InventorySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getInventorySummary()
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch((err) => {
        if (!cancelled) {
          const msg = getApiErrorMessage(err, t("widget_error"));
          setError(msg);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleClick = () => navigate("/inventory");

  if (loading) {
    return (
      <button
        type="button"
        onClick={handleClick}
        className="w-full text-left flex flex-col items-center justify-center py-8 text-center hover:opacity-90 transition-opacity"
      >
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mb-3" />
        <p className="text-sm text-neutral-500 dark:text-neutral-400">{t("widget_loading")}</p>
      </button>
    );
  }

  if (error) {
    return (
      <button
        type="button"
        onClick={handleClick}
        className="w-full text-left py-4 text-center hover:opacity-90 transition-opacity"
      >
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      </button>
    );
  }

  if (!summary || summary.total_items === 0) {
    return (
      <button
        type="button"
        onClick={handleClick}
        className="w-full text-left py-4 text-center hover:opacity-90 transition-opacity"
      >
        <p className="text-sm text-neutral-500 dark:text-neutral-400">{t("widget_empty")}</p>
      </button>
    );
  }

  const needsAttention = summary.low_stock + summary.out_of_stock;

  return (
    <button
      type="button"
      onClick={handleClick}
      className="w-full text-left space-y-2 hover:opacity-90 transition-opacity"
    >
      <div className="grid grid-cols-1 gap-2">
        <div className="flex justify-between items-baseline gap-2">
          <span className="text-xs text-neutral-500 dark:text-neutral-400">{t("widget_total_skus")}</span>
          <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">
            {summary.total_items}
          </span>
        </div>
        <div className="flex justify-between items-baseline gap-2">
          <span className="text-xs text-neutral-500 dark:text-neutral-400">
            {t("widget_needs_attention")}
          </span>
          <span
            className={`text-sm font-semibold tabular-nums ${
              needsAttention > 0
                ? "text-amber-600 dark:text-amber-400"
                : "text-neutral-900 dark:text-neutral-100"
            }`}
          >
            {needsAttention}
          </span>
        </div>
        <div className="flex justify-between items-baseline gap-2 pt-1 border-t border-neutral-200 dark:border-neutral-700">
          <span className="text-xs font-medium text-neutral-600 dark:text-neutral-300">
            {t("widget_total_value")}
          </span>
          <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">
            {formatCurrency(summary.total_value)}
          </span>
        </div>
      </div>
    </button>
  );
}
